import posthog from 'posthog-js'
import { initPostHog, isPostHogInitialized } from '@/lib/posthog-init'

declare global {
  interface Window {
    gtag: (...args: unknown[]) => void
  }
}

export const CONSENT_STORAGE_KEY = 'hipelife.analyticsConsent'
export const OPEN_COOKIE_SETTINGS_EVENT = 'hipelife:open-cookie-settings'
export const ANALYTICS_CONSENT_EVENT = 'hipelife:analytics-consent-changed'

export type AnalyticsConsent = 'accepted' | 'declined'

export function getAnalyticsConsent(): AnalyticsConsent | null {
  if (typeof window === 'undefined') return null
  const value = window.localStorage.getItem(CONSENT_STORAGE_KEY)
  return value === 'accepted' || value === 'declined' ? value : null
}

function clearPostHogPersistence(): void {
  if (typeof window === 'undefined') return

  const removePostHogKeys = (storage: Storage): void => {
    Object.keys(storage)
      .filter((key) => key.startsWith('ph_') || key.toLowerCase().includes('posthog'))
      .forEach((key) => storage.removeItem(key))
  }

  removePostHogKeys(window.localStorage)
  removePostHogKeys(window.sessionStorage)

  const host = window.location.hostname
  const hostParts = host.split('.')
  const cookieDomains = new Set<string | null>([null, host, `.${host}`])
  if (hostParts.length > 1) {
    cookieDomains.add(`.${hostParts.slice(-2).join('.')}`)
  }

  document.cookie
    .split(';')
    .map((cookie) => cookie.trim().split('=')[0])
    .filter((name) => name.startsWith('ph_') || name.toLowerCase().includes('posthog'))
    .forEach((name) => {
      cookieDomains.forEach((domain) => {
        const domainPart = domain ? `; domain=${domain}` : ''
        document.cookie = `${name}=; Max-Age=0; path=/${domainPart}; SameSite=Lax`
      })
    })
}

function clearGACookies(): void {
  if (typeof window === 'undefined') return

  const measurementId = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID?.trim()
  if (measurementId) {
    ;(window as unknown as Record<string, boolean>)[`ga-disable-${measurementId}`] = true
  }

  document.cookie
    .split(';')
    .map((cookie) => cookie.trim().split('=')[0])
    .filter((name) => name.startsWith('_ga') || name === '_gid')
    .forEach((name) => {
      document.cookie = `${name}=; Max-Age=0; path=/; SameSite=Lax`
    })
}

export function updateGAConsent(
  consentType: 'analytics_storage' | 'ad_storage',
  value: 'granted' | 'denied'
): void {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('consent', 'update', { [consentType]: value })

    if (process.env.NODE_ENV === 'development') {
      console.log(`[GA4] Consent updated: ${consentType} = ${value}`)
    }
  } else if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
    console.warn('[GA4] Cannot update consent: window.gtag is not available.')
  }
}

export function updatePostHogConsent(granted: boolean): void {
  if (typeof window === 'undefined') return

  if (granted) {
    if (!isPostHogInitialized()) {
      initPostHog()
    }
    posthog.opt_in_capturing()
  } else {
    posthog.opt_out_capturing()
    if (isPostHogInitialized()) {
      posthog.reset(true)
    }
    clearPostHogPersistence()
    window.setTimeout(clearPostHogPersistence, 0)
    window.setTimeout(clearPostHogPersistence, 500)
  }
}

export function updateAllConsent(analyticsGranted: boolean, marketingGranted: boolean): void {
  updateGAConsent('analytics_storage', analyticsGranted ? 'granted' : 'denied')
  updateGAConsent('ad_storage', marketingGranted ? 'granted' : 'denied')
  updatePostHogConsent(analyticsGranted)

  if (!analyticsGranted) {
    clearGACookies()
  }
}

export function setAnalyticsConsent(consent: AnalyticsConsent): void {
  if (typeof window === 'undefined') return

  window.localStorage.setItem(CONSENT_STORAGE_KEY, consent)
  updateAllConsent(consent === 'accepted', false)

  window.dispatchEvent(
    new CustomEvent(ANALYTICS_CONSENT_EVENT, { detail: { consent } })
  )
}

export function openCookieSettings(): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(OPEN_COOKIE_SETTINGS_EVENT))
}

function hasAnalyticsConsent(): boolean {
  return getAnalyticsConsent() === 'accepted'
}

export function trackEvent(
  eventName: string,
  properties?: Record<string, unknown>
): void {
  if (typeof window === 'undefined' || !hasAnalyticsConsent()) {
    return
  }

  if (isPostHogInitialized()) {
    posthog.capture(eventName, properties)
  }

  if (window.gtag) {
    window.gtag('event', eventName, properties)

    if (process.env.NODE_ENV === 'development') {
      console.log(`[GA4] Event tracked: ${eventName}`, properties)
    }
  } else if (process.env.NODE_ENV === 'development') {
    console.warn(
      `[GA4] Cannot track event "${eventName}": window.gtag is not available.`
    )
  }
}

export function trackFormSubmission(
  formName: string,
  success: boolean,
  properties?: Record<string, unknown>
): void {
  trackEvent('form_submission', {
    form_name: formName,
    success,
    ...properties,
  })
}

export function trackCalculatorUse(
  calculatorType: string,
  inputs: Record<string, unknown>
): void {
  trackEvent('calculator_calculate', {
    calculator_type: calculatorType,
    ...inputs,
  })
}

export function trackArticleClick(
  articleSlug: string,
  category: string,
  source: string
): void {
  trackEvent('article_click', {
    article_slug: articleSlug,
    article_category: category,
    click_source: source,
  })
}

export function trackCategoryFilter(category: string): void {
  trackEvent('category_filter', { category })
}
